/**
 * Gestion des disponibilités artistes.
 * Un artiste est considéré indisponible à une date donnée s'il est déjà
 * engagé sur une prestation au statut "Signé", "Réalisée" ou "Contrat Transmis"
 * à cette même date.
 */
import { supabase } from '@/integrations/supabase/client';

const BLOCKING_STATUTS = ['Signé', 'Réalisée', 'Contrat Transmis'] as const;

export interface ConflitArtiste {
  prestationId: string;
  numero: string | null;
  clientNom: string | null;
  statut: string;
  dateEvent: string;
}

/**
 * Retourne une Map<artiste_id, conflit> des artistes indisponibles à la date donnée.
 * @param dateEvent — date ISO (YYYY-MM-DD)
 * @param excludePrestationId — ignore cette prestation (utile en mode édition)
 */
export async function getArtistesIndisponibles(
  dateEvent: string | null | undefined,
  excludePrestationId?: string | null,
): Promise<Map<string, ConflitArtiste>> {
  const result = new Map<string, ConflitArtiste>();
  if (!dateEvent) return result;

  let q = supabase
    .from('prestations')
    .select('id, numero, client_nom, statut, date_event')
    .eq('date_event', dateEvent)
    .in('statut', BLOCKING_STATUTS as unknown as any)
    .is('deleted_at', null);

  if (excludePrestationId) q = q.neq('id', excludePrestationId);

  const { data: prests, error } = await q;
  if (error || !prests?.length) return result;

  const prestIds = prests.map((p: any) => p.id);
  const byPrest: Record<string, any> = {};
  prests.forEach((p: any) => { byPrest[p.id] = p; });

  const { data: liens } = await supabase
    .from('prestations_artistes')
    .select('artiste_id, prestation_id')
    .in('prestation_id', prestIds)
    .not('artiste_id', 'is', null);

  (liens || []).forEach((l: any) => {
    if (l.artiste_id && !result.has(l.artiste_id)) {
      const p = byPrest[l.prestation_id];
      result.set(l.artiste_id, {
        prestationId: l.prestation_id,
        numero: p?.numero ?? null,
        clientNom: p?.client_nom ?? null,
        statut: p?.statut ?? '',
        dateEvent,
      });
    }
  });

  return result;
}

export function formatConflitMessage(c: ConflitArtiste, artisteName?: string): string {
  const who = artisteName ? `${artisteName} est` : 'Cet artiste est';
  const ref = c.numero ? ` (${c.numero}${c.clientNom ? ' — ' + c.clientNom : ''})` : '';
  const d = new Date(c.dateEvent);
  const dateStr = isNaN(d.getTime()) ? c.dateEvent : d.toLocaleDateString('fr-FR');
  return `${who} déjà booké sur une prestation au statut "${c.statut}" le ${dateStr}${ref}.`;
}
